import { useCallback, useEffect, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  AlertTriangle, ArrowLeft, Building2, Clock, Download, Globe, MessageSquare, Play, RefreshCw,
  Scale, ShieldAlert, ShieldOff, Trash2, Users,
} from 'lucide-react'
import { api } from '../lib/api'
import { formatDate, formatDateTime } from '../lib/format'
import { Card, CardBody, CardHeader, SectionHeading, StatTile } from '../components/ui/Card'
import { Chip } from '../components/ui/Badge'
import Button from '../components/ui/Button'
import Modal, { ConfirmDialog } from '../components/ui/Modal'
import { TextField } from '../components/ui/Field'
import { SkeletonStats, SkeletonText } from '../components/ui/Skeleton'
import { useToast } from '../components/ui/Toast'

const MODULE_ICONS = {
  grievance: MessageSquare,
  feedback: MessageSquare,
  survey: Users,
  posh: Scale,
  whistleblower: ShieldAlert,
  integration: Globe,
}

/**
 * Everything a platform admin might need to know about one client
 * organisation — registry details, per-module case counts, admin roster
 * size and suspension history. POSH and whistleblower numbers stop at
 * aggregate counts: no case, complainant or hearing detail ever reaches this
 * screen, matching how AuditChain itself excludes administrators from those
 * two trails.
 */
export default function TenantDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const toast = useToast()

  const [tenant, setTenant] = useState(null)
  const [migrating, setMigrating] = useState(false)
  const [confirming, setConfirming] = useState(null) // 'deactivate' | 'reactivate' | null
  const [reason, setReason] = useState('')
  const [working, setWorking] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const [deleteCode, setDeleteCode] = useState('')
  const [deleteError, setDeleteError] = useState(null)
  const [deleteSubmitting, setDeleteSubmitting] = useState(false)
  const [backingUp, setBackingUp] = useState(false)

  const load = useCallback(async () => {
    try {
      setTenant(await api.get(`/platform/tenants/${id}`))
    } catch (err) {
      toast.error(err.message)
      navigate('/tenants', { replace: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id])

  useEffect(() => { setTenant(null); load() }, [load])

  async function migrate() {
    setMigrating(true)
    try {
      const result = await api.post(`/platform/tenants/${id}/migrate`)
      toast.success(result?.message ?? 'Migrated.')
      load()
    } catch (err) {
      toast.error(err.message)
    } finally {
      setMigrating(false)
    }
  }

  async function toggleActive() {
    setWorking(true)
    try {
      if (tenant.is_active) {
        await api.post(`/platform/tenants/${id}/deactivate`, { reason: reason.trim() || undefined })
      } else {
        await api.post(`/platform/tenants/${id}/reactivate`)
      }
      toast.success(tenant.is_active ? 'Tenant suspended.' : 'Tenant reactivated.')
      setConfirming(null)
      setReason('')
      load()
    } catch (err) {
      toast.error(err.message)
    } finally {
      setWorking(false)
    }
  }

  async function downloadBackup() {
    setBackingUp(true)
    try {
      await api.download(`/platform/tenants/${id}/backup`, `${tenant.code}-backup.sql`)
      toast.success('Backup downloaded.')
    } catch (err) {
      toast.error(err.message)
    } finally {
      setBackingUp(false)
    }
  }

  async function confirmDelete() {
    setDeleteError(null)

    if (deleteCode.trim() !== tenant.code) {
      setDeleteError('That does not match this tenant\'s code.')
      return
    }

    setDeleteSubmitting(true)
    try {
      await api.delete(`/platform/tenants/${id}`, { data: { code: deleteCode.trim() } })
      toast.success(`${tenant.name} has been permanently deleted.`)
      navigate('/tenants', { replace: true })
    } catch (err) {
      setDeleteError(err.message)
    } finally {
      setDeleteSubmitting(false)
    }
  }

  if (!tenant) {
    return (
      <div className="space-y-5">
        <SkeletonText lines={2} />
        <SkeletonStats count={4} />
      </div>
    )
  }

  const stats = tenant.stats
  const moduleEntries = stats ? Object.entries(stats.modules) : []

  return (
    <div className="space-y-5">
      <Link to="/tenants" className="inline-flex items-center gap-1.5 text-sm text-[var(--color-ink-500)] hover:text-[var(--color-ink-900)]">
        <ArrowLeft size={14} aria-hidden="true" />
        All tenants
      </Link>

      <SectionHeading
        level={1}
        title={
          <span className="flex items-center gap-2">
            {tenant.name}
            {!tenant.is_active && <Chip tone="warning">Suspended</Chip>}
          </span>
        }
        description={`${tenant.code} · ${tenant.legal_name}`}
        actions={
          <>
            <Button variant="secondary" icon={RefreshCw} onClick={migrate} loading={migrating}>
              Apply pending migrations
            </Button>
            <Button
              variant={tenant.is_active ? 'dangerOutline' : 'primary'}
              icon={tenant.is_active ? ShieldOff : Play}
              onClick={() => setConfirming(tenant.is_active ? 'deactivate' : 'reactivate')}
            >
              {tenant.is_active ? 'Suspend' : 'Reactivate'}
            </Button>
          </>
        }
      />

      {!stats ? (
        <div
          role="alert"
          className="rounded-md border px-3 py-2.5 text-sm"
          style={{ borderColor: 'var(--color-warning)', backgroundColor: 'var(--color-warning-bg)', color: 'var(--color-ink-900)' }}
        >
          This tenant's database could not be reached, so usage statistics are unavailable. Registry details below are still accurate.
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatTile icon={Users} label="Employees" value={stats.employees.total} hint={`${stats.employees.active} active`} />
          <StatTile icon={Users} label="Administrators" value={stats.admins} />
          <StatTile
            icon={Clock}
            label="Last activity"
            value={stats.last_activity_at ? formatDate(stats.last_activity_at) : '—'}
          />
          <StatTile icon={Building2} label="Modules enabled" value={moduleEntries.length} hint={`of ${Object.keys(tenant.available_modules).length}`} />
        </div>
      )}

      <div className="grid gap-5 lg:grid-cols-3">
        <Card className="lg:col-span-2">
          <CardHeader title="Modules" description="Case volume per module. POSH and whistleblower show counts only, never case detail." />
          <CardBody padded={false}>
            {!stats ? (
              <div className="p-5"><SkeletonText lines={4} /></div>
            ) : moduleEntries.length === 0 ? (
              <div className="p-5 text-sm text-[var(--color-ink-500)]">No modules enabled for this tenant.</div>
            ) : (
              <ul className="divide-y divide-[var(--color-line)]">
                {moduleEntries.map(([key, data]) => {
                  const Icon = MODULE_ICONS[key] ?? Building2

                  return (
                    <li key={key} className="flex items-center justify-between gap-3 px-5 py-3.5">
                      <div className="flex items-center gap-2.5">
                        <Icon size={16} className="text-[var(--color-ink-500)]" aria-hidden="true" />
                        <span className="text-sm font-medium text-[var(--color-ink-900)]">
                          {tenant.available_modules[key] ?? key}
                        </span>
                      </div>
                      <div className="flex items-center gap-4 text-sm tnum">
                        {'open' in data && (
                          <span className="text-[var(--color-ink-500)]">{data.open} open</span>
                        )}
                        {'sla_breached' in data && data.sla_breached > 0 && (
                          <Chip tone="danger">{data.sla_breached} breached SLA</Chip>
                        )}
                        {'responses' in data && (
                          <span className="text-[var(--color-ink-500)]">{data.responses} responses</span>
                        )}
                        <span className="font-semibold text-[var(--color-ink-900)]">{data.total} total</span>
                      </div>
                    </li>
                  )
                })}
              </ul>
            )}
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Organisation" />
          <CardBody>
            <dl className="space-y-3 text-sm">
              <Row label="Code" value={<span className="font-mono">{tenant.code}</span>} />
              <Row label="Database" value={<span className="font-mono">{tenant.db_name}</span>} />
              <Row label="Subdomain" value={tenant.subdomain} />
              <Row label="Timezone" value={tenant.timezone} />
              <Row label="Currency" value={tenant.currency} />
              <Row label="Languages" value={(tenant.locales ?? []).join(', ')} />
              <Row
                label="Brand colour"
                value={
                  <span className="inline-flex items-center gap-1.5">
                    <span
                      className="h-3.5 w-3.5 rounded-full border border-[var(--color-line-strong)]"
                      style={{ backgroundColor: tenant.primary_color ?? '#000' }}
                      aria-hidden="true"
                    />
                    <span className="font-mono text-xs">{tenant.primary_color ?? '—'}</span>
                  </span>
                }
              />
              <Row label="Contact name" value={tenant.primary_contact_name ?? '—'} />
              <Row label="Contact email" value={tenant.primary_contact_email ?? '—'} />
              <Row label="Contact phone" value={tenant.primary_contact_phone ?? '—'} />
              <Row label="Onboarded" value={tenant.onboarded_at ? formatDate(tenant.onboarded_at) : '—'} />
              <Row label="Last updated" value={tenant.updated_at ? formatDateTime(tenant.updated_at) : '—'} />
              {!tenant.is_active && (
                <>
                  <Row label="Suspended" value={tenant.suspended_at ? formatDateTime(tenant.suspended_at) : '—'} />
                  {tenant.suspended_reason && <Row label="Reason" value={tenant.suspended_reason} />}
                </>
              )}
              {tenant.notes && <Row label="Notes" value={tenant.notes} />}
            </dl>
          </CardBody>
        </Card>
      </div>

      <Card>
        <CardHeader
          title="Danger zone"
          description="Permanently delete this tenant and everything in its database. There is no undo."
          actions={
            <Button variant="danger" icon={Trash2} onClick={() => { setDeleting(true); setDeleteCode(''); setDeleteError(null) }}>
              Delete tenant
            </Button>
          }
        />
      </Card>

      <ConfirmDialog
        open={confirming === 'reactivate'}
        onClose={() => setConfirming(null)}
        onConfirm={toggleActive}
        title="Reactivate this tenant?"
        message="Sign-in will be restored for everyone at this organisation."
        confirmLabel="Reactivate"
        loading={working}
      />

      <Modal
        open={confirming === 'deactivate'}
        onClose={() => setConfirming(null)}
        title="Suspend this tenant?"
        size="sm"
        dismissable={!working}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirming(null)} disabled={working}>Cancel</Button>
            <Button variant="danger" onClick={toggleActive} loading={working}>Suspend</Button>
          </>
        }
      >
        <div className="space-y-3">
          <p className="text-sm leading-relaxed text-[var(--color-ink-700)]">
            Nobody at this organisation will be able to sign in. Their data is untouched, and this can be undone at any time.
          </p>
          <TextField
            label="Reason"
            hint="Optional — shown on this tenant's detail page while suspended."
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>
      </Modal>

      <Modal
        open={deleting}
        onClose={() => setDeleting(false)}
        title="Delete this tenant permanently?"
        size="sm"
        dismissable={!deleteSubmitting}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDeleting(false)} disabled={deleteSubmitting}>Cancel</Button>
            <Button
              variant="danger"
              onClick={confirmDelete}
              loading={deleteSubmitting}
              disabled={deleteCode.trim() !== tenant.code}
            >
              Delete permanently
            </Button>
          </>
        }
      >
        <div className="space-y-4">
          <div
            role="alert"
            className="flex gap-2.5 rounded-md border px-3 py-2.5 text-sm leading-relaxed"
            style={{ borderColor: 'var(--color-danger)', backgroundColor: 'var(--color-danger-bg)', color: 'var(--color-danger)' }}
          >
            <AlertTriangle size={16} className="mt-0.5 shrink-0" aria-hidden="true" />
            <span>
              This permanently deletes <strong>{tenant.name}</strong>'s entire database — every
              grievance, POSH complaint, whistleblower report and employee record. This cannot be
              undone.
            </span>
          </div>

          <Button
            variant="secondary"
            icon={Download}
            onClick={downloadBackup}
            loading={backingUp}
            className="w-full"
          >
            Download a backup first
          </Button>

          {deleteError && (
            <div
              role="alert"
              className="rounded-md border px-3 py-2.5 text-sm"
              style={{ borderColor: 'var(--color-danger)', backgroundColor: 'var(--color-danger-bg)', color: 'var(--color-danger)' }}
            >
              {deleteError}
            </div>
          )}

          <TextField
            label={`Type "${tenant.code}" to confirm`}
            value={deleteCode}
            onChange={(event) => setDeleteCode(event.target.value)}
            autoComplete="off"
            autoFocus
          />
        </div>
      </Modal>
    </div>
  )
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-3">
      <dt className="shrink-0 text-[var(--color-ink-500)]">{label}</dt>
      <dd className="text-right font-medium text-[var(--color-ink-900)]">{value}</dd>
    </div>
  )
}
